import React, { useEffect, useState } from 'react';
import { Col, Container, Dropdown, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import UserDefinedField from '../../components/Fields/UserDefinedField';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import BrandModel from '../../models/BrandModel';
import UserDefinedFieldValueModel from '../../models/UserDefinedFieldValueModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { Result } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';
import EditBrandReferencesPopup from './Popups/Brand_popup_EditReferences';
import ExportBrandPopup from './Popups/Brand_popup_Export';
import Brand_tab_Employees from './Tabs/Brand_tab_Employees';
import Brand_tab_Items from './Tabs/Brand_tab_Items';
import Brand_tab_SubBrands from './Tabs/Brand_tab_SubBrands';

const BrandView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const [showEditBrandReferencesPopup, setShowEditBrandReferencesPopup] = useState(false)
    const [showExportBrandPopup, setShowExportBrandPopup] = useState(false)
    const { handleResult, handleException } = useNotifications();
    const navigate = useNavigate()

    const [formData, setFormData] = useState<BrandModel>({
        id: 0,
        name: '',
        abbreviation: '',
        parentBrandId: 0,
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        concurrencyId: '',
        userDefinedFields: [],
    });

    const brands = useSelectListOptions('api/v1/selectlist/BrandNameTrees', '-- Select brand');

    let idParams: IdParams = useParams();
    let brandId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        brandId = parseInt(idParams.id);
    }

    useEffect(() => {
        getFormData()
    }, [brandId]);

    function getFormData() {
        if (brandId !== undefined) {
            DataService.getById<BrandModel>('api/v1/brands/', brandId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
        else {
            DataService.get<UserDefinedFieldValueModel[]>('api/v1/branduserdefinedfields/fornewbrand/')
                .then(response => {
                    formData.userDefinedFields = [...response];
                    setFormData({ ...formData });
                })
                .catch(e => handleException(e));
        }
    }

    function submit() {
        if (formData) {
            if (formData.id === 0)
                DataService
                    .create<number>(`api/v1/brands/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            navigate('/plm/itemsetup/brand/'+ response.data)
                    })
                    .catch(e => handleException(e));
            else 
                DataService
                    .update<BrandModel>(`api/v1/brands/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        getFormData();
                    })
                    .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'abbreviation', label: 'Abbreviation' },
            { name: 'parentBrandId', label: 'Parent brand' },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    if (formData && formData.userDefinedFields) {
        formData.userDefinedFields.forEach((value, index) => {
            let label = value.userDefinedField.name
            formConfig.fields.push({ name: 'userDefinedFields.' + index + '.value', label: label });
        });
    }

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (brandId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditBrand)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem width={230} icon="fa fa-image" title="Change logo" appFunction={ApplicationFunctions.EditBrand} disabled />
            <ActionMenuItem icon="fa fa-edit" title="Change reference codes" appFunction={ApplicationFunctions.EditBrand} onClick={() => setShowEditBrandReferencesPopup(true)} />
            <Dropdown.Divider />
            <ActionMenuItem icon="fa fa-sign-out" title="Export brand" appFunction={ApplicationFunctions.EditBrand} onClick={() => setShowExportBrandPopup(true)} />
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form title='Brand' actions={formActions} footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditBrand}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        {formData.thumbnailUrl && (
                            <Col md='2'>
                                <img src={formData.thumbnailUrl} style={{ width: '100%' }} />
                            </Col>
                        )}
                        <Col md='10'>
                            <TextboxField {...getFieldProps('name')} />
                            <TextboxField {...getFieldProps('abbreviation')} />
                            <SelectListField {...getFieldProps('parentBrandId')} options={brands} />
                            {formData && formData.userDefinedFields &&
                                formData.userDefinedFields.map((value, index) => {
                                    let name = 'userDefinedFields.' + index + '.value';
                                    return (<UserDefinedField {...getFieldProps(name)} userDefinedFieldValue={value} key={'userDefinedField' + index} />);
                                })
                            }
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                    </Row>
                </Container>
            </Form>
            {brandId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='vtabs'
                                defaultActiveKey='subBrands'
                                tabs={[
                                    {
                                        title: 'Sub-Brands',
                                        eventKey: 'subBrands',
                                        component: <Brand_tab_SubBrands id={brandId} />,
                                        applicationFunction: ApplicationFunctions.ViewEmployee
                                    },
                                    {
                                        title: 'Items',
                                        eventKey: 'items',
                                        component: <Brand_tab_Items id={brandId} />,
                                        applicationFunction: ApplicationFunctions.ViewEmployee
                                    },
                                    {
                                        title: 'Employees',
                                        eventKey: 'employees',
                                        component: <Brand_tab_Employees id={brandId} ></Brand_tab_Employees>,
                                        applicationFunction: ApplicationFunctions.ViewEmployee
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
            {showEditBrandReferencesPopup && <EditBrandReferencesPopup brand={formData} show={showEditBrandReferencesPopup} onHide={() => setShowEditBrandReferencesPopup(false)} />}
            {showExportBrandPopup && <ExportBrandPopup brand={formData} show={showExportBrandPopup} onHide={() => setShowExportBrandPopup(false)} />}
        </div>
    );
}
export default BrandView;